import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// actions
import { fetchPosts, fetchWidgets } from 'src/modules/community/communityHome/actions';
import { fetchCurrentCommunity, fetchFeaturedPosts } from 'src/modules/shared/context/actions';
import { validateTag } from 'src/api/tag';

// components
import { CommunityLayout } from 'src/layouts';
import CommunityHomeContent from 'src/modules/community/communityHome/components/communityHomeContent';
import CommunityHomeWidgets from 'src/modules/community/communityHome/components/widgets/communityHomeWidgets';
import WithSession from 'src/components/hoc/withSession';
import WithErrorHandling, { RequestError } from 'src/components/hoc/withErrorHandling';
import NHSContentModal from 'src/pages/tag/NHSContentModal';
import AdvertModalContainer from 'src/components/advertModalContainer';
import IframeModal from 'src/modules/shared/modal/components/iframeModal';

// utils
import { getCommunitySeo } from 'src/utils/seo';
import { combinePostsWithFeaturedPosts } from 'src/utils/featuredPosts';
import { trackFootfallEvent } from 'src/utils/footfall';
import footfallEventTypes from 'src/constants/footfallEventTypes';
import { slugify } from 'underscore.string';
import modalTypes from 'src/constants/modalTypes';

const isServer = typeof window === 'undefined';

const fetchPostsWithFilter = (currentUser, communitySlug) => {
  return fetchPosts(communitySlug, !currentUser ? 'popular' : 'latest',  !currentUser);
};

class CommunityHomePage extends React.Component {
  static propTypes = {
    community: PropTypes.object,
    currentUser: PropTypes.object,
    fetchFeaturedPosts: PropTypes.func,
    fetchPostsWithFilter: PropTypes.func,
    lang: PropTypes.string,
    widgets: PropTypes.array,
  };

  static defaultProps = {
    community: null,
    currentUser: null,
    fetchFeaturedPosts: () => {},
    fetchPostsWithFilter: () => {},
    lang: '',
    widgets: [],
  };

  static async getInitialProps({ api, res, store: { dispatch, getState }, query: { slug }, currentUser}) {
    await dispatch(fetchCurrentCommunity(slug));
    const state = getState();
    const { currentCommunity, communityErrorStatusCode } = state.context;

    if (!currentCommunity) {
      const isHiddenPrivateCommunity = communityErrorStatusCode === 310;
      if (res) {
        // 314 code for community not found
        if (communityErrorStatusCode === 314) {
          // We first check if the slug is an ontology tag
          const tagName = slugify(slug);
          const response = await validateTag(api, tagName);

          // Tag exists in ontology
          if (response.success) {
            res.writeHead(301, { Location: `/tag/${tagName}` }).end();
          }

          // Tag isn't in ontology but there is an associated new ontology tag
          if (!response.success && response.redirect) {
            res.writeHead(301, { Location: response.location }).end();
          }

          // Slug isn't a tag and could be: deleted community (314), deleted tag
          // (410 for no associated new tag), username, pathname to a BG page or
          // neither one of them (404). We let BG handle redirections
          if (!response.success) {
            // custom code to be able to redirect to BG
            res.writeHead(399, { Location: `/${slug}` }).end();
          }
        } else if (isHiddenPrivateCommunity) {
          if (!currentUser) {
            throw new RequestError('This page is not available to visitors', 401);
          } else {
            throw new RequestError('This is a private community', 404); // user is not a member
          }
        }
      }
      return {};
    }

    const requests = [
      dispatch(fetchPostsWithFilter(currentUser, slug)),
      dispatch(fetchWidgets(slug)),
    ].filter(x => !!x);

    await Promise.all(requests);

    return {};
  }

  componentDidMount() {
    const { community, currentUser, fetchFeaturedPosts, lang } = this.props;

    if (!isServer && !!community) {
      trackFootfallEvent(footfallEventTypes.VIEWED_COMMUNITY, { communityPath: community.slug });
      fetchFeaturedPosts({
        communityId: community.communityId,
        userType: currentUser ? 'member' : 'visitor',
        countryId: 'all',
        language: lang,
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { currentUser, community, fetchPostsWithFilter } = this.props;
    // [BB-1787] We want to make sure that the default filter is set correctly after the user logs in
    if (currentUser !== prevProps.currentUser) {
      fetchPostsWithFilter(currentUser, community.slug);
    }
  }

  render() {
    const { community, widgets, modalType, modalProps } = this.props;

    return (
      <React.Fragment>
        <CommunityLayout
          containerClassName="community-home__container"
          currentPage="home"
          seo={getCommunitySeo(community)}
          sideBarContent={<CommunityHomeWidgets community={community} widgets={widgets} />}
        >
          <CommunityHomeContent {...this.props} />
          <AdvertModalContainer currentPage="home" />
        </CommunityLayout>
        { modalType === modalTypes.NHS_TAG_CONTENT &&
          <NHSContentModal tagName={modalProps.tagName} />
        }
        { modalType === modalTypes.IFRAME &&
          <IframeModal />
        }
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  const { currentUser, currentCommunity } = state.context;
  const { posts, filter, widgets, isPostListLoading } = state.pages.communityHome;
  let combinedPosts = posts;
  if (currentCommunity && currentCommunity.featuredPosts) {
    const { featuredPosts, dismissedFeaturedPostIds } = currentCommunity;
    const dismissedIds = dismissedFeaturedPostIds || [];
    const filteredFeaturedPosts = featuredPosts
      .filter(featuredPost => !dismissedIds.includes(featuredPost.featuredPostId));
    combinedPosts = combinePostsWithFeaturedPosts(posts.slice(0, 5), filteredFeaturedPosts)
  }

  return {
    community: currentCommunity,
    currentUser,
    combinedPosts,
    filter,
    widgets,
    isPostListLoading,
    modalType: state.modal.modalType,
    modalProps: state.modal.modalProps,
  };
};

const actions = {
  fetchPosts,
  fetchPostsWithFilter,
  fetchFeaturedPosts,
};

export default connect(mapStateToProps, actions)(
  WithErrorHandling(WithSession(CommunityHomePage)),
);
